import React, { useEffect, useState, useMemo, useCallback } from 'react'
import { Box, Paper } from '@mui/material'
import { editObject, getObjects, getObject } from '../../../api/apiClientCrud'
import useNavigate from '../../hooks/useNavigate'
import Form from '../../form/Form'
import { useAuth } from '../../../context/AuthContext'
import { useLocation, useOutletContext } from 'react-router-dom'
import Toolbar from '../../toolbar/Toolbar'
import 'dayjs/locale/fr'
import SubModule from '../../module/SubModule'
import { getRoleFields } from './RoleFields'
import { useFetchEntities } from '../../hooks/useFetchEntities'
import useSaveEntity from '../../hooks/useSaveEntity'
import UserSubModule from '../../subModules/UserSubModule'

const ROUTES = {
  EDIT: '/roles/edit',
  VIEW: '/roles/view',
  ADD: '/roles/add'
}

const buildPayload = ({ data, user, mode }) => {
  const isEdit = mode === 'edit'
  return {
    ...data,
    ...(isEdit
      ? { lastModBy: user.email }
      : {
          company_id: user.company_id,
          createdBy: user.email
        })
  }
}

const Role = () => {
  const { user } = useAuth()
  const history = useNavigate()

  const [role, setRole] = useState({ access_rights: [], company_id: user.company_id })
  const [modulesList, setModulesList] = useState([])
  const [isSubmit, setIsSubmit] = useState(false)

  const userRight = useOutletContext()
  const location = useLocation()
  const path = location.pathname
  const roleId = location.state?.id

  const mode = useMemo(() => {
    if (path === ROUTES.EDIT) return 'edit'
    if (path === ROUTES.VIEW) return 'view'
    if (path === ROUTES.ADD) return 'add'
    return null
  }, [path])

  const formMode = mode === 'add' ? 'edit' : mode

  const setAccessRights = useCallback((newLines) => setRole((prev) => ({ ...prev, access_rights: newLines })), [])

  const roleQuery =
    (mode === 'edit' || mode === 'view') && roleId ? { key: 'role', fn: getObject, args: ['roles', roleId] } : null

  const queries = [{ key: 'modules', fn: getObjects, args: ['modules', {}] }, roleQuery].filter(Boolean)

  const { data: fetched, loading: loadingEntities } = useFetchEntities(queries)

  const { saveEntity, loading } = useSaveEntity({
    entityName: 'roles',
    mode,
    id: roleId,
    user,
    editFn: editObject,
    contractLineName: 'Roles',
    args: [role],
    onSuccessRedirect: (id) => history(location.state?.history || ROUTES.VIEW, { state: { id } }),
    onSuccess: (res) => {
      setRole({
        ...res.data,
        access_rights: res.data.access_rights ?? []
      })
      setIsSubmit(false)
    },
    onError: () => setIsSubmit(false),
    buildPayload
  })

  const memoizedFields = useMemo(
    () => getRoleFields(user, role, modulesList, setAccessRights),
    [user, role, modulesList, setAccessRights]
  )

  const options = useMemo(
    () => ({
      fields: memoizedFields,
      formStyle: { width: '90%', margin: '0 auto' },
      data: role
    }),
    [memoizedFields, role]
  )

  const toolbar = useMemo(
    () => ({
      accessRight: userRight,
      objectType: 'roles',
      title: () => {
        if (path.includes('/add')) return 'Nouveau Rôle'
        const hasName = role?.name
        if (hasName) {
          return `Rôle ${role.name ?? ''}`
        }
        // Fallback when data not yet loaded
        return 'Rôle'
      },
      actions: { edit: true, back: true, audits: true, list: true }
    }),
    [userRight, path, role]
  )

  const subModules = useMemo(
    () => ({
      tabsNames: [{ tabName: 'Utilisateurs', tabIndex: '1' }],
      tabs: [
        {
          tabIndex: '1',
          component: () => <UserSubModule location={location} user={user} userRight={userRight} />
        }
      ]
    }),
    [location, user, userRight]
  )

  useEffect(() => {
    if (!loadingEntities && fetched) {
      if (fetched.modules) setModulesList(fetched.modules)
      if (fetched.role) {
        setRole({
          ...fetched.role,
          access_rights: fetched.role.access_rights ?? []
        })
      }
    }
  }, [fetched, loadingEntities])

  useEffect(() => {
    if (modulesList.length > 0) {
      const missingRights = modulesList
        .filter((module) => !(role.access_rights ?? []).some((right) => right.module_id === module.id))
        .map((module) => ({
          module_id: module.id,
          role_id: location.state?.id,
          id_tmp: crypto.randomUUID?.() || `${module.id}-${Date.now()}`
        }))

      if (missingRights.length > 0) {
        setRole((prev) => ({
          ...prev,
          access_rights: [...(prev.access_rights ?? []), ...missingRights]
        }))
      }
    }
  }, [modulesList, location.state?.id, role.access_rights])

  useEffect(() => {
    if (isSubmit) {
      const cleanedRole = {
        ...role,
        access_rights: (role.access_rights ?? []).map((line) => ({ ...line }))
      }
      saveEntity({ data: cleanedRole })
    }
  }, [isSubmit])

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 65px)',
        overflowY: 'auto',
        flex: 1
      }}
    >
      <Toolbar options={toolbar} object={role} />

      <Form
        mode={formMode}
        options={options}
        setDataParent={setRole}
        setIsSubmitParent={setIsSubmit}
        loading={loading}
      />

      {mode === 'view' && (
        <Box sx={{ margin: '10px' }}>
          <Paper sx={{ width: '90%', margin: '0 auto' }}>
            <SubModule options={subModules} />
          </Paper>
        </Box>
      )}
    </Box>
  )
}

export default Role
